import SortOrder from '~/client/src/shared/enums/SortOrder'

import { buildBandTree } from './bands'
import type { FacetIndex } from './facets'
import { buildFacetIndex, filterByFacets, resolveOptionLabel } from './facets'
import { flattenTree } from './flatten'
import type { BandSelectionState, ListModel, SelectionModel } from './model'
import type { ListQuery } from './query'
import type { ListSchema, ResolvedBandLevel } from './schema'
import { resolveBandLevels } from './schema'
import { filterBySearch } from './search'
import {
  bandSelectionState,
  reconcileSelection,
  selectionSummary,
} from './selection'
import { sortRecords } from './sort'

/** @public */
export function buildListModel<T>(
  records: readonly T[],
  schema: ListSchema<T>,
  query: ListQuery,
  allRecords: readonly T[] = records,
): ListModel<T> {
  return createListPipeline<T>()(records, schema, query, allRecords)
}

/**
 * Creates a reference-memoized list pipeline. Same-reference array membership
 * changes are detected shallowly; record field changes require observable
 * access through ListStore or immutable record/array replacement.
 */
export function createListPipeline<T>(): (
  records: readonly T[],
  schema: ListSchema<T>,
  query: ListQuery,
  allRecords?: readonly T[],
) => ListModel<T> {
  const stableRecords = stabilizeArrayInput<T>()
  const stableAllRecords = stabilizeArrayInput<T>()
  const facetMemo = memoizeLast(buildFacetIndex)
  const searchMemo = memoizeLast(filterBySearch)
  const facetFilterMemo = memoizeLast(filterByFacets)
  const selectionMemo = memoizeLast(reconcileSelection)
  const sortMemo = memoizeLast(sortRecords)
  const bandLevelsMemo = memoizeLast(resolveBandLevels)
  const bandTreeMemo = memoizeLast(
    (
      sortedRecords: readonly T[],
      bandLevels: readonly ResolvedBandLevel<T>[],
      getId: (record: T) => string,
      labelsByField: FacetIndex['labelsByField'],
      emptyOptionLabel: string | undefined,
      bandSortKey: string | null,
      bandSortOrder: SortOrder,
    ) =>
      buildBandTree(
        sortedRecords,
        bandLevels,
        getId,
        labelsByField,
        emptyOptionLabel,
        bandSortKey,
        bandSortOrder,
      ),
  )
  const flattenMemo = memoizeLast(flattenTree)
  const facetsMemo = memoizeLast(buildFacets)
  const selectionModelMemo = memoizeLast(buildSelectionModel)
  const bandSelectionMemo = memoizeLast(buildBandSelection)
  const assembleMemo = memoizeLast(assembleModel)

  return (records, schema, query, allRecords = records) => {
    const sorting = resolveSorting(query)
    const recordsInput = stableRecords(records)
    const allRecordsInput =
      allRecords === records ? recordsInput : stableAllRecords(allRecords)
    const index = facetMemo(allRecordsInput, schema)
    const searchedRecords = searchMemo(recordsInput, schema, query.search)
    const filteredRecords = facetFilterMemo(
      searchedRecords,
      schema,
      index,
      query.appliedFacets,
    )
    const selection = selectionMemo(allRecordsInput, schema, query.selectedIds)
    const sortedRecords = sortMemo(
      filteredRecords,
      schema,
      sorting.recordSortKey,
      sorting.recordSortOrder,
    )
    const bandLevels = bandLevelsMemo(schema, query.groupBy)
    const tree = bandTreeMemo(
      sortedRecords,
      bandLevels,
      schema.getId,
      index.labelsByField,
      schema.emptyOptionLabel,
      sorting.bandSortKey,
      sorting.bandSortOrder,
    )
    const flattened = flattenMemo(tree, query.collapsedBandIds)
    const selectionModel = selectionModelMemo(
      selection,
      filteredRecords,
      schema.getId,
    )
    const bandSelection = bandSelectionMemo(flattened, selection.selectedIds)

    return assembleMemo(
      index.sourceMapByFacet,
      facetsMemo(schema, index.labelsByField),
      filteredRecords,
      flattened,
      selectionModel,
      bandSelection,
    )
  }
}

function stabilizeArrayInput<T>(): (records: readonly T[]) => readonly T[] {
  let previousSource: readonly T[] | undefined
  let previousMembers: readonly T[] | undefined
  let stable: readonly T[] | undefined

  return records => {
    if (
      records === previousSource &&
      previousMembers !== undefined &&
      records.length === previousMembers.length &&
      records.every((record, index) => record === previousMembers?.[index])
    ) {
      return stable ?? records
    }

    const sourceChanged = previousSource !== records
    previousSource = records
    previousMembers = records.slice()
    stable = sourceChanged ? records : records.slice()
    return stable
  }
}

function resolveSorting(query: ListQuery): {
  readonly recordSortKey: string | null
  readonly recordSortOrder: SortOrder
  readonly bandSortKey: string | null
  readonly bandSortOrder: SortOrder
} {
  const bandSortKey = query.groupBy.includes(query.sortKey ?? '')
    ? query.sortKey
    : null

  return {
    recordSortKey: bandSortKey ? null : query.sortKey,
    recordSortOrder: bandSortKey ? SortOrder.DEFAULT : query.sortOrder,
    bandSortKey,
    bandSortOrder: bandSortKey ? query.sortOrder : SortOrder.DEFAULT,
  }
}

function buildFacets<T>(
  schema: ListSchema<T>,
  labelsByField: FacetIndex['labelsByField'],
): ListModel<T>['facets'] {
  return schema.fields
    .filter(field => field.facetable)
    .map(field => ({
      key: field.key,
      label: field.label,
      optionLabel: (id: string) =>
        resolveOptionLabel(
          labelsByField[field.key],
          id,
          field.emptyOptionLabel ?? schema.emptyOptionLabel,
        ),
    }))
}

function assembleModel<T>(
  sourceMapByFacet: ListModel<T>['sourceMapByFacet'],
  facets: ListModel<T>['facets'],
  filteredRecords: readonly T[],
  flattened: ReturnType<typeof flattenTree<T>>,
  selection: SelectionModel<T>,
  bandSelection: ListModel<T>['bandSelection'],
): ListModel<T> {
  return {
    nodes: flattened.nodes,
    filteredRecords,
    filteredCount: filteredRecords.length,
    facets,
    sourceMapByFacet,
    bandIds: flattened.bandIds,
    isAllCollapsed: flattened.isAllCollapsed,
    selection,
    bandSelection,
  }
}

function buildSelectionModel<T>(
  selection: ReturnType<typeof reconcileSelection<T>>,
  filteredRecords: readonly T[],
  getId: (record: T) => string,
): SelectionModel<T> {
  const filteredSelectableIds = filteredRecords.map(record => getId(record))
  const summary = selectionSummary(filteredSelectableIds, selection.selectedIds)

  return {
    count: selection.selectedIds.size,
    selectedRecords: selection.selectedRecords,
    selectedIds: selection.selectedIds,
    filteredSelectableIds: summary.filteredSelectableIds,
    allFilteredSelected: summary.allFilteredSelected,
    someFilteredSelected: summary.someFilteredSelected,
  }
}

function buildBandSelection<T>(
  flattened: ReturnType<typeof flattenTree<T>>,
  selectedIds: ReadonlySet<string>,
): ReadonlyMap<string, BandSelectionState> {
  const selection = new Map<string, BandSelectionState>()

  for (const node of flattened.nodes) {
    if (node.kind !== 'band') continue
    selection.set(node.id, bandSelectionState(node.selectableIds, selectedIds))
  }

  return selection
}

function memoizeLast<Args extends readonly unknown[], R>(
  fn: (...args: Args) => R,
): (...args: Args) => R {
  let prevArgs: Args | undefined
  let prevR: R | undefined
  let hasValue = false
  return (...args) => {
    if (
      hasValue &&
      prevArgs !== undefined &&
      args.length === prevArgs.length &&
      args.every((arg, index) => arg === prevArgs?.[index])
    ) {
      return prevR as R
    }
    prevArgs = args
    prevR = fn(...args)
    hasValue = true
    return prevR
  }
}
